import { useEffect } from 'react';
import { getSession } from '../utils/auth';

export default function DataBrowserPage({ C, useBreakpoint, Link }) {
	const { isMobile, isTablet, px } = useBreakpoint();
	const compact = isMobile || isTablet;

	return (
		<div
			style={{
				paddingTop: 100,
				minHeight: '80vh',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			{/* ── HEADER ── */}
			<div style={{ padding: `60px ${px} 48px` }}>
				<div className='section-label fade-up' style={{ marginBottom: 20 }}>
					Data Browser
				</div>
				<h1
					className='display-heading fade-up delay-1'
					style={{
						fontSize: compact ? 'clamp(30px,7vw,52px)' : 'clamp(38px,5vw,60px)',
						marginBottom: 14,
					}}
				>
					Temporarily unavailable.
				</h1>
				<p
					style={{
						fontSize: compact ? 14 : 16,
						color: '#8AACAA',
						maxWidth: 580,
						lineHeight: 1.9,
					}}
				>
					The individual record browser is temporarily offline while we complete
					a data licensing review. The research database and its methodology
					remain intact.
				</p>
			</div>

			{/* ── NOTICE CARD ── */}
			<div style={{ padding: `0 ${px}`, marginBottom: 48 }}>
				<div
					style={{
						background: '#111E1C',
						border: '1px solid #1A2E2A',
						borderLeft: `3px solid ${C.teal}`,
						padding: compact ? '28px 24px' : '36px 40px',
						maxWidth: 720,
					}}
				>
					<div
						style={{
							fontFamily: 'DM Mono',
							fontSize: 10,
							letterSpacing: '0.15em',
							textTransform: 'uppercase',
							color: C.tealLight,
							marginBottom: 16,
						}}
					>
						What's happening
					</div>
					<p
						style={{
							fontSize: compact ? 14 : 15,
							color: '#8AACAA',
							lineHeight: 1.9,
							marginBottom: 18,
						}}
					>
						In the interest of full transparency: we are working through a data
						licensing question with IPUMS, the source of the census microdata
						underlying this research. Out of an abundance of caution, individual
						records have been taken offline until that review is complete.
					</p>
					<p
						style={{
							fontSize: compact ? 14 : 15,
							color: '#6A8A86',
							lineHeight: 1.9,
							margin: 0,
						}}
					>
						The heatmap, summary statistics, household composition data, and
						full methodology remain available. Individual records will return —
						in a form that fully satisfies all licensing requirements — as soon
						as that process is complete.
					</p>
				</div>
			</div>

			{/* ── WHAT REMAINS AVAILABLE ── */}
			<div style={{ padding: `0 ${px}`, marginBottom: 60 }}>
				<div
					style={{
						fontFamily: 'DM Mono',
						fontSize: 10,
						letterSpacing: '0.15em',
						textTransform: 'uppercase',
						color: C.tealLight,
						marginBottom: 20,
					}}
				>
					Still available
				</div>
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
						gap: 2,
					}}
				>
					{[
						{
							title: 'Disruption Heatmap',
							desc: 'Street-level visualization of census linkage failures across the Greenwood District.',
							link: '/heatmap',
							label: 'View Heatmap →',
						},
						{
							title: 'Summary Statistics',
							desc: '878 households, 1,739 persons in the active risk pool. Household composition data and risk distribution.',
							link: '/explore',
							label: 'Explore Data →',
						},
						{
							title: 'Full Methodology',
							desc: 'The complete Census Disruption Model whitepaper, including all filters, findings, and ethical framework.',
							link: '/whitepaper',
							label: 'Read Whitepaper →',
						},
					].map(({ title, desc, link, label }) => (
						<div
							key={title}
							style={{
								background: '#111E1C',
								border: '1px solid #1A2E2A',
								padding: compact ? '24px 20px' : '28px 24px',
								display: 'flex',
								flexDirection: 'column',
								gap: 12,
							}}
						>
							<div
								style={{
									fontWeight: 500,
									fontSize: 15,
									color: 'white',
									fontFamily: 'Playfair Display',
								}}
							>
								{title}
							</div>
							<p
								style={{
									fontSize: 13,
									color: '#4A6A66',
									lineHeight: 1.7,
									flex: 1,
									margin: 0,
								}}
							>
								{desc}
							</p>
							<Link
								to={link}
								style={{
									fontFamily: 'DM Mono',
									fontSize: 10,
									color: C.tealLight,
									letterSpacing: '0.1em',
									textDecoration: 'none',
									textTransform: 'uppercase',
									display: 'flex',
									alignItems: 'center',
									gap: 6,
									transition: 'color 0.15s',
									marginTop: 4,
								}}
								onMouseEnter={(e) => (e.currentTarget.style.color = 'white')}
								onMouseLeave={(e) =>
									(e.currentTarget.style.color = C.tealLight)
								}
							>
								{label}
							</Link>
						</div>
					))}
				</div>
			</div>

			{/* ── CONTACT ── */}
			<div style={{ padding: `0 ${px} 80px` }}>
				<div
					style={{
						background: '#070F0D',
						border: '1px solid #1A2E2A',
						borderTop: `3px solid ${C.teal}`,
						padding: compact ? '28px 24px' : '36px 40px',
						display: 'flex',
						flexDirection: compact ? 'column' : 'row',
						justifyContent: 'space-between',
						alignItems: compact ? 'flex-start' : 'center',
						gap: 24,
					}}
				>
					<div>
						<div
							style={{
								fontFamily: 'DM Mono',
								fontSize: 10,
								letterSpacing: '0.15em',
								textTransform: 'uppercase',
								color: C.tealLight,
								marginBottom: 10,
							}}
						>
							Research Inquiries
						</div>
						<p
							style={{
								fontSize: 14,
								color: '#6A8A86',
								lineHeight: 1.7,
								maxWidth: 440,
								margin: 0,
							}}
						>
							If you are a researcher, descendant, or institution with a
							specific need to access records during this period, reach out
							directly.
						</p>
					</div>
					<a
						href='mailto:thecenterfortheuncounted@censusdisruption.org?subject=Data Access Inquiry'
						style={{
							background: C.teal,
							border: 'none',
							cursor: 'pointer',
							padding: '14px 28px',
							color: 'white',
							fontSize: 12,
							fontFamily: 'DM Mono',
							letterSpacing: '0.1em',
							textTransform: 'uppercase',
							whiteSpace: 'nowrap',
							textDecoration: 'none',
							flexShrink: 0,
						}}
					>
						Get in Touch →
					</a>
				</div>
			</div>
		</div>
	);
}
